import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Phone,
  Home,
  Briefcase,
  PoundSterling,
  Users,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuarantorVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line_1: '',
    city: '',
    postcode: '',
    employer_name: '',
    annual_income: '',
    relationship: ''
  });
  
  // Verify token on load
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/tenancy-lifecycle/guarantor/verify?token=${token}`);
        const data = await response.json();
        
        if (data.valid) {
          setTokenValid(true);
          setInviteDetails(data);
          // Pre-fill name and email
          const nameParts = (data.guarantor_name || '').split(' ');
          setFormData(prev => ({
            ...prev,
            first_name: nameParts[0] || '',
            last_name: nameParts.slice(1).join(' ') || '',
            email: data.guarantor_email || ''
          }));
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        console.error('Verification error:', error);
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);
  
  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.address_line_1 || !formData.city || !formData.postcode) {
      toast.error('Please provide your full address');
      return;
    }
    
    if (!formData.relationship) {
      toast.error('Please specify your relationship to the tenant');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/guarantor/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ...formData,
          annual_income: formData.annual_income ? parseFloat(formData.annual_income) : null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Your details have been submitted successfully!');
      } else {
        toast.error(data.detail || 'Failed to submit details');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Verifying your invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // No token or invalid token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-gray-600 mb-6">
              This guarantor verification link is invalid or has expired. Please contact the tenant to request a new invitation.
            </p>
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-2">
              Your guarantor details have been submitted successfully.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              The tenant will be notified that you have completed the verification. 
              You may now close this page.
            </p>
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main form
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="guarantor-verification-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Guarantor Verification</h1>
          <p className="text-gray-600 mt-2">
            Please complete this form to act as a guarantor
          </p>
        </div>
        
        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Application Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Tenant:</span>
                <p className="font-medium">{inviteDetails?.tenant_name}</p>
              </div>
              <div>
                <span className="text-gray-500">Property:</span>
                <p className="font-medium">{inviteDetails?.property_address}</p>
              </div>
            </div>
            
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 mb-2">Important Information</h4>
              <p className="text-sm text-amber-700">
                As a guarantor, you agree to be legally responsible for paying rent and any damages 
                if the tenant is unable to do so. Please ensure you understand this commitment before proceeding.
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Details</CardTitle>
              <CardDescription>
                Fields marked with * are required
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-10"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="pl-10"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Your Address
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="address_line_1">Address *</Label>
                  <Input
                    id="address_line_1"
                    name="address_line_1"
                    value={formData.address_line_1}
                    onChange={handleChange}
                    required
                    data-testid="address-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postcode">Postcode *</Label>
                    <Input
                      id="postcode"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      required
                      data-testid="postcode-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Employment */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Employment Details
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employer_name">Employer Name</Label>
                    <Input
                      id="employer_name"
                      name="employer_name"
                      value={formData.employer_name}
                      onChange={handleChange}
                      data-testid="employer-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="annual_income">Annual Income (£)</Label>
                    <div className="relative">
                      <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="annual_income"
                        name="annual_income"
                        type="number"
                        value={formData.annual_income}
                        onChange={handleChange}
                        className="pl-10"
                        placeholder="e.g. 45000"
                        data-testid="income-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Relationship */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Relationship to Tenant
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="relationship">How are you related to the tenant? *</Label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleChange}
                    className="w-full border rounded-lg p-3"
                    required
                    data-testid="relationship-select"
                  >
                    <option value="">-- Select relationship --</option>
                    <option value="parent">Parent</option>
                    <option value="guardian">Guardian</option>
                    <option value="grandparent">Grandparent</option>
                    <option value="sibling">Sibling</option>
                    <option value="aunt_uncle">Aunt/Uncle</option>
                    <option value="employer">Employer</option>
                    <option value="friend">Family Friend</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              {/* Consent */}
              <div className="bg-gray-50 border rounded-lg p-4">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    className="mt-1"
                    data-testid="consent-checkbox"
                  />
                  <span className="text-sm text-gray-700">
                    I confirm that I am willing to act as a guarantor and understand that I will be 
                    legally responsible for the rent and any damages if the tenant fails to pay. 
                    I consent to the landlord/agency performing credit and identity checks.
                  </span>
                </label>
              </div>
            </CardContent>
            
            <CardFooter className="border-t">
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full"
                data-testid="submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Guarantor Details'
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </div>
  );
}
